import { Component } from 'react';
import { Card, Button } from 'react-bootstrap';
import { GoX } from 'react-icons/go';
import Tooltip from "../../../controls/common/tooltip";
import { Converter } from "../../workspace/converter";
import SessionContext from "../../session/context";
import WidgetStatus from "./widgetStatus";
import "./index.css";

/**
 * Removes a widget from the playground definition: drops the node, drops any connections that
 * referenced it (free-form only — grid playgrounds carry no connections, so the filter is a no-op),
 * and clears the details pane.
 *
 * Exported because there are now three ways to remove a widget — the header "×" and the Delete key on
 * each of the two workspace layouts — and they must all take exactly the same path.
 */
export function removeComponentById(shellInterface, componentId) {
  if ( !shellInterface || !shellInterface.getState || !componentId ) return;
  const state = shellInterface.getState();
  const workflowInterface = state.workflowInterface;
  const nextNodes = (state.nodes || []).filter((n) => n.id !== componentId);
  if ( workflowInterface && workflowInterface.setNodes ) {
    workflowInterface.setNodes(nextNodes, /*isModified=*/true);
  }
  const nextEdges = (state.edges || []).filter(
    (e) => e.source !== componentId && e.target !== componentId
  );
  if ( workflowInterface && workflowInterface.setEdges && nextEdges.length !== (state.edges || []).length ) {
    workflowInterface.setEdges(nextEdges, /*isModified=*/true);
  }
  if ( shellInterface.updateDetails ) {
    shellInterface.updateDetails(null, null);
  }
}

export default class WidgetBase extends Component {
  static contextType = SessionContext;

  // Last-resort free-form dimensions, used only when neither the component nor its manifest
  // carries a sizing port. Matches the most common manifest defaults.
  static FALLBACK_WIDTH = 700;
  static FALLBACK_HEIGHT = 500;

  constructor(props) {
    super(props);
    this.subscribedAliases = new Set();
    this.discoveredAliases = new Set();
    this.cleanupFns = {};
  }

  loadObjectValue(component, route, type) {
    const alias = Converter.getComponentPortValueData(component, route);
    if ( !alias ) {
      return [null, { variant: "info", message: `Connect a value to the "${route}" port.` }];
    }

    // Use context which is SessionManager; also support explicit static usage (backward compat).
    const sessionManager = this.context;
    const manager = sessionManager || arguments[0];

    if ( !manager || !manager.session ) {
      return [null, { variant: "info", message: "Select a session to view this widget." }];
    }

    const value = manager.getSessionData(alias);

    // Register alias for subscription
    this.discoveredAliases.add(alias);

    if ( !value ) {
      return [null, { variant: "info", message: `"${alias}" is not available in the selected session yet.` }];
    }

    const valueType = value["type"];
    if ( type !== valueType ) {
      return [null, {
        variant: "warning",
        message: `The "${route}" port expects a "${type}" value, but "${alias}" is "${valueType}".`
      }];
    }

    return [value, null];
  }

  componentDidMount() {
    this.updateSubscriptions();
    this.sessionListener = () => this.forceUpdate();
    if (this.context) {
      this.context.addListener(this.sessionListener);
    }
  }

  componentDidUpdate() {
    this.updateSubscriptions();
  }

  componentWillUnmount() {
    // Unsubscribe all
    for (const alias in this.cleanupFns) {
      this.cleanupFns[alias]();
    }
    this.cleanupFns = {};
    this.subscribedAliases.clear();

    if (this.context && this.sessionListener) {
      this.context.removeListener(this.sessionListener);
    }
    this.sessionListener = null;
  }

  updateSubscriptions() {
    const manager = this.context;
    if (!manager) return;

    // Unsubscribe from stale
    for (const alias of this.subscribedAliases) {
      if (!this.discoveredAliases.has(alias)) {
        if (this.cleanupFns[alias]) {
          this.cleanupFns[alias]();
          delete this.cleanupFns[alias];
        }
        this.subscribedAliases.delete(alias);
      }
    }

    // Subscribe to new
    for (const alias of this.discoveredAliases) {
      if (!this.subscribedAliases.has(alias)) {
        this.cleanupFns[alias] = manager.subscribe(alias, () => {
          this.forceUpdate();
        });
        this.subscribedAliases.add(alias);
      }
    }

    // Reset discovered for next render
    this.discoveredAliases.clear();
  }

  renderNotice(notice) {
    if ( !notice ) return null;
    // Pass the variant straight through. General widget alerts use info/warning
    // (standard Alert styling, consistent with the rest of the app);
    // content-internal callers pass "light" for the subtle box.
    return <WidgetStatus variant={notice.variant || "info"}>{notice.message}</WidgetStatus>;
  }

  renderLoading(message) {
    // Loading is a general widget state -> primary Alert (matches the app-wide
    // "Loading …" convention, e.g. "Loading revision"), with an inline spinner.
    return <WidgetStatus variant="primary" loading>{message}</WidgetStatus>;
  }

  renderBody() {
    return (
      <Card.Body>
        Widget content implementation is missing.
      </Card.Body>
    );
  }

  getClassName() {
    return "";
  }

  /**
   * Resolve a sizing port (width / height) to a positive number of pixels.
   *
   * Ports are materialized onto a component when it is dropped onto the canvas, so a playground
   * saved BEFORE its widget's manifest declared width/height has no port to read. Fall back to
   * the manifest default in that case, and to a sane constant if even that is missing — anything
   * else yields "nullpx", which the browser drops, leaving the widget unsized.
   */
  getSizePortValue(route, fallback) {
    const value = Converter.getComponentPortValueData(this.props.data.component, route);
    if (typeof value === "number" && value > 0) {
      return value;
    }
    const portManifest = (this.props.data?.manifest?.ports || []).find((p) => p["route"] === route);
    const defaultValue = portManifest?.["value"]?.["default"];
    return (typeof defaultValue === "number" && defaultValue > 0) ? defaultValue : fallback;
  }

  // The free-form `width` port sizes the CARD, not the body — see getEffectiveSize. Null in grid,
  // where the cell owns the width.
  getConfiguredWidth() {
    if (this.props.data?.layoutType === "grid") {
      return null;
    }
    return `${this.getSizePortValue("width", WidgetBase.FALLBACK_WIDTH)}px`;
  }

  // Content ALWAYS fills its card.
  //
  // The width port used to be applied to the body while the card was left to shrink-wrap. But a card
  // can't be narrower than its header's minimum, and the header carries a name, an alias chip, action
  // buttons, a type label and a close button — so on any widget configured narrower than that floor,
  // the card came out wider than the body and the content sat in a column with dead space beside it.
  // Putting the width on the card instead means the body tracks it whatever it resolves to.
  //
  // Height stays on the body: nothing competes with it there, and the card has no height floor of its
  // own to fight.
  getEffectiveSize() {
    if (this.props.data?.layoutType === "grid") {
      return { width: "100%", height: "100%", maxHeight: "100%" };
    }
    const h = this.getSizePortValue("height", WidgetBase.FALLBACK_HEIGHT);
    return { width: "100%", height: `${h}px`, maxHeight: `${h}px` };
  }

  renderContent() {
    const { width, height, maxHeight } = this.getEffectiveSize();

    return (
      <Card.Body className="nodrag nowheel" style={{
        width,
        height,
        maxHeight,
        overflow: "scroll"
      }}>
        {this.renderBody()}
      </Card.Body>
    );
  }

  isSelected() {
    const shellInterface = this.props.data?.shellInterface;
    if (!shellInterface || !shellInterface.getState) return false;
    const state = shellInterface.getState();
    const componentId = this.props.data?.component?.id;
    return state?.details?.view === "component" && state?.details?.data?.id === componentId;
  }

  removeComponent = (event) => {
    event.stopPropagation();
    removeComponentById(this.props.data?.shellInterface, this.props.data?.component?.id);
  };

  renderHeaderControls() {
    const canEdit = !!this.props.data?.shellInterface?.canEditRevision?.();
    return (
      <span className="playground-widget-controls float-right" onClick={(e) => e.stopPropagation()}>
        <span className="text-muted playground-widget-type">
          {this.props.data.component.type}
        </span>
        {canEdit && (
          <Tooltip text="Remove widget">
            <Button
              variant="light"
              size="sm"
              onClick={this.removeComponent}
            >
              <GoX />
            </Button>
          </Tooltip>
        )}
      </span>
    );
  }

  // Override points for envelope-backed widgets (Thread, Message). Default
  // to null so generic widgets keep their current header unchanged.
  renderHeaderAlias() {
    return null;
  }

  renderHeaderActions() {
    return null;
  }

  render() {
    // Clear discovered aliases at the beginning of render so subscription
    // accumulation starts fresh on each pass.
    this.discoveredAliases.clear();

    const isGrid = this.props.data?.layoutType === "grid";
    const isSelected = this.isSelected();
    const cardClassName = [
      "playground-widget",
      this.getClassName() || "",
      isGrid ? "playground-widget-grid" : "",
      isSelected ? "playground-widget-selected" : "",
    ].filter(Boolean).join(" ");

    // The header is a permanent in-flow top bar on every widget — always
    // rendered (not just on selection); see common/index.css.
    const renderHeader = true;

    // Per-widget content zoom (grid mode only). Comes from the playground
    // shell's zoom slider — the layout itself stays at 1x so widget cells
    // remain aligned to the grid; only what's inside each cell scales.
    const widgetZoom = this.props.data?.zoom ?? 1;
    const contentStyle = isGrid ? { zoom: widgetZoom } : undefined;

    const configuredWidth = this.getConfiguredWidth();

    return (
      <Card className={cardClassName} style={configuredWidth ? { width: configuredWidth } : undefined}>
        {renderHeader && (
          <Card.Header>
            <span className="playground-widget-header-left">
              <span className="playground-widget-name">
                {this.props.data.component.name}
              </span>
              {this.renderHeaderAlias()}
            </span>
            <span className="playground-widget-header-right">
              {this.renderHeaderActions()}
              {this.renderHeaderControls()}
            </span>
          </Card.Header>
        )}
        <div className="playground-widget-content" style={contentStyle}>
          {this.renderContent()}
        </div>
      </Card>
    );
  }
}
